/**
 * U.S. Social Security Number validation (XXX-XX-XXXX).
 * Keep in sync with Portal-Backend/services/usSsnValidation.js
 */

export function extractSsnDigits(raw: unknown): string {
  return String(raw ?? "").replace(/\D/g, "");
}

/** Format up to 9 digits for display with hyphens. */
export function formatSsnDisplay(digits: string): string {
  const d = digits.slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

export type UsSsnValidationResult =
  | { ok: true; normalized: string }
  | { ok: false; message: string };

export function validateUsSsn(raw: unknown): UsSsnValidationResult {
  const digits = extractSsnDigits(raw);

  if (digits.length !== 9) {
    return {
      ok: false,
      message: "Enter a valid 9-digit SSN (XXX-XX-XXXX).",
    };
  }

  const area = digits.slice(0, 3);
  const group = digits.slice(3, 5);
  const serial = digits.slice(5, 9);

  if (area === "000" || area === "666" || area.startsWith("9")) {
    return {
      ok: false,
      message: "Enter a valid SSN. That area number is not allowed.",
    };
  }
  if (group === "00") {
    return {
      ok: false,
      message: "Enter a valid SSN. That group number is not allowed.",
    };
  }
  if (serial === "0000") {
    return {
      ok: false,
      message: "Enter a valid SSN. That serial number is not allowed.",
    };
  }
  if (/^(\d)\1{8}$/.test(digits)) {
    return { ok: false, message: "Enter a valid SSN." };
  }

  return { ok: true, normalized: digits };
}
